import { createRoot } from "react-dom/client";

// Two very different failures can reject a dynamic import():
//   1. A *stale chunk* — after a deploy, Vite rotates the content-hashed chunk filenames,
//      so a tab still running the previous build requests a chunk that no longer exists.
//      This is transient and a single reload (to pick up the fresh index.html + chunk map)
//      fixes it.
//   2. A *real error* — a module-evaluation throw or a missing/renamed export. This recurs
//      on every load; reloading just hides the bug behind a blank page.
// Only case 1 should reload. Everything else must be surfaced, never swallowed.
const STALE_CHUNK_PATTERNS = [
  "Failed to fetch dynamically imported module",
  "error loading dynamically imported module",
  "Importing a module script failed",
  "Failed to load module script",
];

/**
 *
 * @param error
 */
function isStaleChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return STALE_CHUNK_PATTERNS.some((pattern) => message.includes(pattern));
}

// Reload once to pick up the fresh build. The sessionStorage guard prevents a reload loop
// when a chunk is genuinely broken rather than just stale.
/**
 *
 */
function reloadForStaleChunk(): void {
  const KEY = "staleChunkReloadedAt";
  const last = Number(sessionStorage.getItem(KEY) || 0);
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(KEY, String(Date.now()));
  window.location.reload();
}

// Lazy route chunks (React Router `lazy: () => import(...)`) load through Vite's preload
// helper, which dispatches this event when a chunk fails to load. `event.payload` is the
// original rejection. Only swallow + reload for a true stale chunk; for a real error, leave
// the event un-prevented so Vite re-throws it — that lets the router's errorElement render a
// fallback and lets the console/Sentry see the error instead of silently blanking the route.
window.addEventListener("vite:preloadError", (event) => {
  const payload = (event as unknown as { payload?: unknown }).payload;
  if (isStaleChunkError(payload)) {
    event.preventDefault();
    reloadForStaleChunk();
  }
});

/**
 *
 * @param container
 */
function mountApp(container: HTMLElement): void {
  const root = createRoot(container);
  import("./app")
    .then(({ App }) => {
      root.render(<App />);
    })
    .catch((error) => {
      if (isStaleChunkError(error)) {
        reloadForStaleChunk();
        return;
      }
      // A real boot failure. Surface it instead of leaving #root blank.
      console.error("[bootstrap] Failed to load ./app:", error);
      if (container.childElementCount === 0) {
        container.textContent =
          "Something went wrong while loading the app. Please refresh, and contact support if this persists.";
      }
      // Re-raise on a macrotask so it becomes a top-level error / unhandledrejection that
      // global handlers (and Sentry, once armed) can see, rather than a consumed rejection.
      setTimeout(() => {
        throw error;
      });
    });
}

const container = document.getElementById("root");

// env.json is the primary config source; fetch it first. env.js (which sets
// window._APP_CONFIG synchronously via the <script src="/env.js"> tag in
// index.html) is the fallback, used only when env.json is missing or unparseable.
fetch(`${location.origin}/env.json`)
  .then((response) => {
    if (!response.ok) {
      throw new Error(
        `env.json fetch failed: ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  })
  .then((json) => {
    window._APP_CONFIG = json;
    if (container != null) {
      mountApp(container);
    }
  })
  .catch((error) => {
    // env.json unavailable — fall back to env.js (window._APP_CONFIG).
    if (window._APP_CONFIG) {
      if (container != null) {
        mountApp(container);
      }
      return;
    }
    console.error("[bootstrap] Failed to load app configuration:", error);
    if (container != null && container.childElementCount === 0) {
      container.textContent =
        "Failed to load app configuration. Please refresh, and contact support if this persists.";
    }
  });
